// SCREENS-NEEDS-YOU / SCREEN-HANDOFF: an agent hit a login, 2FA, CAPTCHA or a choice it can't make
// and asks a person (`screen_ask:<id>`). The card reads the ask live (every 10 s while open) and
// shows where it stands, with its one next step:
//   waiting         Open screen (takes over, opens Live on that screen, the person types in the
//                   remote page) · Not now
//   you're in       Open screen (back to it) · Hand back to <agent> (wakes the agent to carry on)
//   handed back     who handed it back, when
//   expired/ended   Start a new screen (asks the agent to open a new one and ask again; once)
// The same actions show on the inbox item (`ScreenAskActions`, compact). Passwords are typed into
// the remote page only: nothing here has a field for them.
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, InfoTip, Spinner, toast, WorkerBadge, workerName } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { MonitorIcon, RotateCcwIcon, TimerIcon, Undo2Icon } from "lucide-react";
import type { Api } from "../../api/client";
import { AskCountdown } from "../agent-screens/ask-countdown";
import { screensKeys } from "../agent-screens/screens-queries";
import { formatRecent } from "../format";
import { problemText } from "../settings/problem-text";
import type { ChatContext } from "./use-chat";

export type ScreenAsk = OperationOutput<"screens.ask">;

export const screenAskKey = (co: string, id: string) =>
  [...screensKeys.all(co), "ask", id] as const;

/** "log in to Maya's LinkedIn so I can shortlist candidates". */
export function screenAskWhat(a: Pick<ScreenAsk, "why" | "account" | "reason">): string {
  const target = a.account?.label ?? "a screen";
  const what =
    a.why === "login"
      ? `log in to ${target}`
      : a.why === "2fa"
        ? `enter the 2FA code for ${target}`
        : a.why === "captcha"
          ? `solve the CAPTCHA on ${target}`
          : `help on ${target}`;
  return a.reason ? `${what} so I can ${a.reason}` : what;
}

export type AskPhase =
  | "waiting"
  | "in_control"
  | "controlled"
  | "handed_back"
  | "declined"
  | "expired"
  | "ended";

/** Where the ask stands for me (`me`: my membership here). */
export function askPhase(a: ScreenAsk, me: string | null): AskPhase {
  if (a.state === "open") {
    if (a.controller) return a.controller.membership_id === me ? "in_control" : "controlled";
    return "waiting";
  }
  if (a.state === "done") return "handed_back";
  return a.state;
}

/** The card's status line for a phase. */
export function askStatus(a: ScreenAsk, phase: AskPhase): string {
  const agent = workerName(a.agent.name);
  const who = a.resolved_by?.name ?? "someone";
  const at = a.resolved_at ? ` at ${formatRecent(a.resolved_at)}` : "";
  switch (phase) {
    case "waiting":
      return a.can_open ? "Waiting for you" : `Waiting for ${a.owner ? a.owner.name : "an admin"}`;
    case "in_control":
      return `You're in control · ${agent} is paused`;
    case "controlled":
      return `${a.controller?.name ?? "Someone"} is in control`;
    case "handed_back":
      return `Handed back to ${agent} by ${who}${at}`;
    case "declined":
      return `Not now · ${who}`;
    case "expired":
      return "Expired: this screen is gone";
    default:
      return "Screen ended";
  }
}

const TONE: Record<AskPhase, "warning" | "info" | "success" | "neutral"> = {
  waiting: "warning",
  in_control: "info",
  controlled: "info",
  handed_back: "success",
  declined: "neutral",
  expired: "neutral",
  ended: "neutral",
};
const BADGE: Record<AskPhase, string> = {
  waiting: "Needs you",
  in_control: "You're in control",
  controlled: "In control",
  handed_back: "Handed back",
  declined: "Not now",
  expired: "Expired",
  ended: "Ended",
};

/** The ask read live (every 10 s while open). */
export function useScreenAsk(api: Api, co: string, askId: string, enabled = true) {
  return useQuery({
    queryKey: screenAskKey(co, askId),
    queryFn: () => api.client.screens.ask({ params: { co, id: askId } }),
    enabled,
    retry: false,
    refetchInterval: (q) => (q.state.data?.state === "open" ? 10_000 : false),
  });
}

/** Live on the ask's screen, in the app (a full page load only outside a router, e.g. a test). */
function useOpenLive(slug: string) {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  return (leaseId: string) => {
    if (router)
      void router.navigate({
        to: "/c/$slug/live",
        params: { slug },
        search: { lease: leaseId },
      } as never);
    else window.location.assign(`/c/${slug}/live?lease=${leaseId}`);
  };
}

/**
 * The ask's buttons for its phase. `compact`: the inbox row (small buttons, one line).
 */
export function ScreenAskActions({
  api,
  co,
  slug,
  ask: a,
  me,
  readOnly = false,
  compact = false,
}: {
  api: Api;
  co: string;
  slug: string;
  ask: ScreenAsk;
  me: string | null;
  readOnly?: boolean;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const openLive = useOpenLive(slug);
  const set = (x: ScreenAsk) => qc.setQueryData(screenAskKey(co, a.id), x);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: screenAskKey(co, a.id) });
    void qc.invalidateQueries({ queryKey: screensKeys.overview(co) });
  };
  const takeOver = useMutation({
    mutationFn: () => api.client.screens.takeOver({ params: { co, id: a.lease_id } }),
    onSuccess: () => {
      refresh();
      openLive(a.lease_id);
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Could not open the screen."), type: "error" }),
  });
  const handBack = useMutation({
    mutationFn: () => api.client.screens.handBack({ params: { co, id: a.lease_id }, body: {} }),
    onSuccess: () => {
      toast.add({ title: `Handed back to ${workerName(a.agent.name)}`, type: "success" });
      refresh();
    },
    onError: (e) => toast.add({ title: problemText(e, "Could not hand it back."), type: "error" }),
  });
  const notNow = useMutation({
    mutationFn: () => api.client.screens.askNotNow({ params: { co, id: a.id } }),
    onSuccess: set,
    onError: (e) => toast.add({ title: problemText(e, "Could not say not now."), type: "error" }),
  });
  const restart = useMutation({
    mutationFn: () => api.client.screens.askRestart({ params: { co, id: a.id } }),
    onSuccess: (x) => {
      set(x);
      toast.add({ title: `Asked ${workerName(a.agent.name)} for a new screen`, type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Could not ask for a new screen."), type: "error" }),
  });
  const extend = useMutation({
    mutationFn: () => api.client.screens.askExtend({ params: { co, id: a.id }, body: {} }),
    onSuccess: (x) => {
      set(x);
      void qc.invalidateQueries({ queryKey: screensKeys.overview(co) });
    },
    onError: (e) => toast.add({ title: problemText(e, "Could not extend."), type: "error" }),
  });
  if (readOnly) return null;
  const phase = askPhase(a, me);
  const size = compact ? "xs" : "sm";
  const agent = workerName(a.agent.name);
  const busy =
    takeOver.isPending ||
    handBack.isPending ||
    notNow.isPending ||
    restart.isPending ||
    extend.isPending;
  // PERSISTENT-SCREENS: more time for a slow 2FA code (+15 min; capped on the server).
  const extendButton = a.can_extend ? (
    <Button
      size={size}
      variant="ghost"
      disabled={busy}
      aria-label="Extend 15 min"
      onClick={() => extend.mutate()}
    >
      <TimerIcon data-icon="inline-start" />
      Extend
    </Button>
  ) : null;
  if (phase === "waiting" && a.can_open)
    return (
      <div className="flex flex-wrap items-center gap-2" data-slot="screen-ask-actions">
        <Button size={size} disabled={busy} onClick={() => takeOver.mutate()}>
          <MonitorIcon data-icon="inline-start" />
          Open screen
        </Button>
        <Button size={size} variant="outline" disabled={busy} onClick={() => notNow.mutate()}>
          Not now
        </Button>
        {extendButton}
      </div>
    );
  if (phase === "in_control")
    return (
      <div className="flex flex-wrap items-center gap-2" data-slot="screen-ask-actions">
        <Button size={size} disabled={busy} onClick={() => openLive(a.lease_id)}>
          <MonitorIcon data-icon="inline-start" />
          Open screen
        </Button>
        <Button size={size} variant="outline" disabled={busy} onClick={() => handBack.mutate()}>
          <Undo2Icon data-icon="inline-start" />
          Hand back to {agent}
        </Button>
        {extendButton}
      </div>
    );
  if ((phase === "expired" || phase === "ended" || phase === "declined") && a.can_restart)
    return (
      <div className="flex flex-wrap items-center gap-2" data-slot="screen-ask-actions">
        <Button size={size} disabled={busy} onClick={() => restart.mutate()}>
          <RotateCcwIcon data-icon="inline-start" />
          Start a new screen
        </Button>
      </div>
    );
  return null;
}

/** A line under the actions once the agent was asked again, or can't be. */
function afterLine(a: ScreenAsk): string | null {
  if (a.state === "open" || a.state === "done") return null;
  if (a.restart_requested_at)
    return `Asked ${workerName(a.agent.name)} for a new screen ${formatRecent(a.restart_requested_at)}`;
  if (!a.agent_active) return `${workerName(a.agent.name)} is no longer active`;
  return null;
}

export function ScreenAskEmbed({ chat, askId }: { chat: ChatContext; askId: string }) {
  const { api, co } = chat;
  const ask = useScreenAsk(api, co, askId, chat.agents);
  if (!chat.agents || ask.isError || (!ask.isPending && !ask.data))
    return (
      <div
        data-slot="screen-ask-card"
        data-state="hidden"
        className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground"
      >
        <MonitorIcon aria-hidden="true" className="size-4" />
        An agent needs a person on a screen
      </div>
    );
  if (ask.isPending)
    return (
      <span
        role="status"
        aria-label="Loading screen ask"
        className="inline-flex items-center gap-2 text-xs text-muted-foreground"
      >
        <Spinner className="size-3" /> Loading…
      </span>
    );
  const a = ask.data;
  const agent = workerName(a.agent.name);
  const heading = `${agent} needs you: ${screenAskWhat(a)}`;
  const phase = askPhase(a, chat.meId);
  const after = afterLine(a);
  return (
    <article
      data-slot="screen-ask-card"
      data-state={a.state}
      data-phase={phase}
      data-ask={a.id}
      aria-label={`Screen ask: ${heading}`}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-card-foreground"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <MonitorIcon aria-hidden="true" className="size-4 shrink-0 text-warning-text" />
        <WorkerBadge kind={a.agent.kind} size="sm" />
        <span className="min-w-0 flex-1 text-base font-medium text-foreground">{heading}</span>
        <InfoTip label="About this ask">
          Open screen shows the agent's browser in Live: you type into the page there, never in
          chat. The screen stays open until {formatRecent(a.expires_at)} (Extend for more time),
          then {agent} is told. A login made there is kept in its saved profile.
        </InfoTip>
        <Badge variant={TONE[phase]} dot={phase === "waiting"} data-slot="screen-ask-badge">
          {BADGE[phase]}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        <p
          data-slot={a.state === "open" ? "screen-ask-waiting" : "screen-ask-resolved"}
          className="text-xs text-muted-foreground"
        >
          {askStatus(a, phase)}
        </p>
        {a.state === "open" ? <AskCountdown expiresAt={a.expires_at} /> : null}
      </div>
      <ScreenAskActions
        api={api}
        co={co}
        slug={chat.company.slug}
        ask={a}
        me={chat.meId}
        readOnly={chat.readOnly}
      />
      {after ? (
        <p data-slot="screen-ask-after" className="text-xs text-muted-foreground">
          {after}
        </p>
      ) : null}
    </article>
  );
}

/** The inbox row's actions for a needs-you card it points at (nothing once it is not an ask). */
export function ScreenAskInboxActions({
  api,
  co,
  slug,
  askId,
  me,
}: {
  api: Api;
  co: string;
  slug: string;
  askId: string;
  me: string | null;
}) {
  const ask = useScreenAsk(api, co, askId);
  if (!ask.data) return null;
  const phase = askPhase(ask.data, me);
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2" data-slot="inbox-screen-ask">
      <Badge variant={TONE[phase]}>{BADGE[phase]}</Badge>
      {ask.data.state === "open" ? <AskCountdown expiresAt={ask.data.expires_at} /> : null}
      <ScreenAskActions api={api} co={co} slug={slug} ask={ask.data} me={me} compact />
    </span>
  );
}
